import styles from './MapPilotageDetailsDialog.module.css';

import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import Moment from 'moment';
import ShipApiRepository from '../../../../repositories/api/ShipApiRepository';
import { Dialog } from '../../dialogs/Dialog';
import { DialogProperties } from '../../dialogs/DialogProperties';
import { BusySpinner } from '../../overlay/components/Busy';
import { isArrayEmpty, isNullOrEmpty, isObjectNull, isNumeric } from '../../../helpers/ObjectHelpers';
import ShipImage from '../../ShipImage';
import TextFieldWrapper from '../../fieldWrappers/TextFieldWrapper';
import Spacer from '../../Spacer';
import Icons from '../../icons/Icons';
import { getPilotAssignmentStatusColorBySystemName, getPilotAssignmentStatusColorRgbaBySystemName, getPilotAssignmentTransportOrderBorderColor } from '../../../../pages/pilot/pilot-assignment/helpers/PilotAssignmentHelpers';
import MapElementService, { IMapElementService } from '../services/MapElementService';
import { MiniCard } from '../../card/MiniCard';
import { Card } from '../../card/Card';
import DmsCoordinates from "dms-conversion";
import { useShowDialog, useSetShowDialog } from '../hooks/useMapStore';


export const MapPilotageDetailsDialog = () => {

    const  showDialog = useShowDialog();
    const  setShowDialog = useSetShowDialog();

    const [isBusy, setIsBusy] = useState(true);
    const [shipDetails, setShipDetails] = useState<any | null>(null);
    const [show, setShow] = useState<boolean>(false);
    const [marker, setMarker] = useState<any | null>(null);

    const onClose = () => {
        setShowDialog(false);
        setMarker(null);
    }

    async function initializeShipDetailAsync(marker:any) {

        if (!Object.hasOwn(marker, "shipId")) return null;
        if (marker.shipId === 0) return null;
        const response:any = await ShipApiRepository.getShipDetailAsync(marker.shipId);
        if (response.ok) {
            const data = await response.json();
            return data;
        }

        return null;
    }

    async function initializeAsync(marker: any) {
        const shipDetails = await initializeShipDetailAsync(marker);

        setShipDetails(shipDetails);
        setIsBusy(false);
    }

    const handleSelectShipPosition = (data: any) => {
        if (data) {
            setShow(true);
        } else {
            setShow(false);
        }

        setMarker(data);

        if (data) {
            initializeAsync(data);
        }
    }



    useEffect(() => {
        handleSelectShipPosition(showDialog);
    }, [showDialog]);

    return (
        (marker && show) &&
        <>
        <Dialog properties={{
            ...DialogProperties,
            size: "large",
            title: getTitle(),
            onClose: onClose,
            actions: [
                {
                    icon: "ok",
                    onClick: onClose,
                    themeColor: "primary",
                    text: "Ok"
                }
            ]
        }}>
            {
                    isBusy ?
                        <div className="google-map-dialog-busy">
                            <BusySpinner />
                        </div>
                        :
                        <>
                            <ShipInformation
                                marker={marker}
                                shipDetails={shipDetails}
                            />

                            <AssignmentInformation
                                shipDetails={shipDetails}/>

                            <PilotTransportOrderInformation
                                shipDetails={shipDetails}/>

                            <PilotInformation shipDetails={shipDetails} />
                        </>
            }

            </Dialog>
        </>
    )

    function getTitle() {
        if (isBusy || !shipDetails) return marker.description;

        return shipDetails.shipInformation.name;
    }
}

interface ShipInformationProps {
    marker: any,
    shipDetails: any
}

const ShipInformation = ({ marker, shipDetails }: ShipInformationProps) => {

    const mapElementService: IMapElementService = new MapElementService();
    const speedOverGround = isNumeric(marker.speedOverGround) ? marker.speedOverGround.toFixed(1) : 0;
    const courseOverGround = isNumeric(marker.courseOverGround) ? marker.courseOverGround.toFixed(1) : 0;
    const dmsCoords = new DmsCoordinates(marker.position.lat(), marker.position.lng());

    const { longitude, latitude } = dmsCoords.dmsArrays;

    const getCoordinateDirection = (nsew: string) => {
        switch (nsew) {
            case "E":
                return "&Oslash;";
            case "W":
                return "V";
            default:
                return nsew;
        }

    }
    const parseCoordinates = (coordinate: any) => {
        const [d, m, s, nsew] = coordinate;
        return (
            parse(`${d}&#176;${m}'${s.toFixed(0)}" ${getCoordinateDirection(nsew)}`)
        )
    }

    return (
        <>
            <ShipImage imoNo={marker.imoNo} description={marker.description} spaceBottom={15} />

            <Card
                title={hasShipDetails() ? mapElementService.formatShipTypeIdentifierName(shipDetails.shipInformation.type) : ""}>
                <div className="map-dialog-container">
                    {
                        hasShipDetails() ?
                            <>
                                <div className={styles.shipInfoGrid}>
                                    <div>
                                        <TextFieldWrapper
                                            title="COG"
                                            boldTitle={true}
                                            text={parse(`${courseOverGround}&#176;`)} />
                                    </div>
                                    <div>
                                        <TextFieldWrapper
                                            title="SOG"
                                            boldTitle={true}
                                            text={`${speedOverGround}kn`} />
                                    </div>
                                    <div>
                                        <TextFieldWrapper
                                            title="Posisjon"
                                            boldTitle={true}
                                            text={`${parseCoordinates(latitude)}, ${parseCoordinates(longitude)}`} />
                                    </div>
                                    <div>
                                        <TextFieldWrapper
                                            title="Kj.sign"
                                            boldTitle={true}
                                            text={shipDetails.shipInformation.callSign} />
                                    </div>
                                    <div>
                                        <TextFieldWrapper
                                            title="Land"
                                            boldTitle={true}
                                            text={shipDetails.shipInformation.country} />
                                    </div>
                                    <div>
                                        <TextFieldWrapper
                                            title="BT"
                                            boldTitle={true}
                                            text={shipDetails.shipInformation.grossTonnage} />
                                    </div>
                                    <div>
                                        <TextFieldWrapper
                                            title="Lengde"
                                            boldTitle={true}
                                            text={shipDetails.shipInformation.length} />
                                    </div>

                                    <div>
                                        <TextFieldWrapper
                                            title="Bredde"
                                            boldTitle={true}
                                            text={shipDetails.shipInformation.breadth} />
                                    </div>

                                    <div>
                                        <TextFieldWrapper
                                            title="Maks DPG"
                                            boldTitle={true}
                                            text={shipDetails.shipInformation.draught} />
                                    </div>
                                </div>
                            </>
                            :
                            <>
                                <div>
                                    <div>
                                        <TextFieldWrapper
                                            title="Kj.sign"
                                            boldTitle={true}
                                            text={marker.callSign} />
                                    </div>
                                    <div/>
                                    <div/>
                                </div>
                                <Spacer height={10} />
                                <div>
                                    <div>
                                        <Icons iconName="warning"/>
                                    </div>
                                    <div style={{
                                        paddingTop: 3
                                    } }>
                                        <b>Fartøyinformasjon er ikke registrert i grunnlagsdata.</b>
                                    </div>
                                </div>
                            </>
                    }

                </div>
                {
                    hasShipContactInformation() &&
                        <>
                            <Spacer height={10} />
                            <div className={styles.shipContactGrid}>
                                <div>
                                    <TextFieldWrapper
                                        title="Telefon"
                                        boldTitle={true}
                                        text={formatContactInformation(shipDetails.shipContactInformation.shipPhoneNo)}
                                        textIsLink={!isNullOrEmpty(shipDetails.shipContactInformation.shipPhoneNo)}
                                    />
                                </div>
                                <div>
                                    <TextFieldWrapper
                                        title="Mobil"
                                        boldTitle={true}
                                        text={formatContactInformation(shipDetails.shipContactInformation.shipMobilePhoneNo)}
                                        textIsLink={!isNullOrEmpty(shipDetails.shipContactInformation.shipMobilePhoneNo)}
                                    />
                                </div>
                                <div>
                                    <TextFieldWrapper
                                        title="Vakttelefon"
                                        boldTitle={true}
                                        text={formatContactInformation(shipDetails.shipContactInformation.shipAssistancePhoneNo)}
                                        textIsLink={!isNullOrEmpty(shipDetails.shipContactInformation.shipAssistancePhoneNo)}
                                    />
                                </div>
                            </div>
                        </>
                }
            </Card>

            <Spacer height={15} />
        </>
    )

    function hasShipDetails() {
        return !isObjectNull(shipDetails);
    }

    function hasShipContactInformation() {
        if (!hasShipDetails()) return false;

        const shipContactInformation = shipDetails.shipContactInformation;
        if (isObjectNull(shipContactInformation)) return false;

        return !isNullOrEmpty(shipContactInformation.shipPhoneNo) ||
            !isNullOrEmpty(shipContactInformation.shipMobilePhoneNo) ||
            !isNullOrEmpty(shipContactInformation.shipAssistancePhoneNo);
    }

    function formatContactInformation(info:string) {
        if (isNullOrEmpty(info)) return "Ikke registrert";
        return parse(info.replace(' ', '&nbsp;'));
    }
}

interface AssignmentInformationProps {
    shipDetails: any
}

const AssignmentInformation = ({ shipDetails }: AssignmentInformationProps) => {
    return (
        (hasPilotage() || hasAgentInformation()) &&
        <Card title="Losoppdrag">
            {
                hasPilotage() &&
                    <>
                        <div className={styles.pilotageInfoGrid}>
                            <div>Fra</div>
                            <div>{shipDetails.pilotageInformation.pilotageFromLocation}</div>
                            <div>Til</div>
                            <div>{shipDetails.pilotageInformation.pilotageToLocation}</div>
                            <div>Start losing</div>
                            <div>{Moment(shipDetails.pilotageInformation.pilotageStartTime).format("DD.MM HH:mm")}</div>
                            <div>Aktuell DPG</div>
                            <div>{shipDetails.pilotageInformation.actualDraft}</div>
                            <div>Airdraft</div>
                            <div>{shipDetails.pilotageInformation.airDraft}</div>
                        </div>
                        <Spacer height={10}/>
                    </>
            }
            {
                hasAgentInformation() &&
                    <div className={styles.agentInfoGrid}>
                        <div>Agent</div>
                        <div>{shipDetails.agentInformation.agentName}</div>
                        <div>Vakttelefon</div>
                        <div>
                            <a href={`tel: ${shipDetails.agentInformation.agentAssistancePhone}`} className="phone-link">
                                {`${shipDetails.agentInformation.agentAssistancePhone}`}
                            </a>
                        </div>
                        <div>Kontaktperson</div>
                        <div>{formatAgentInformation(shipDetails.agentInformation.agentContactPerson)}</div>
                        <div>Telefon</div>
                        <div>{formatAgentInformation(shipDetails.agentInformation.agentContactPhone)}</div>
                    </div>
            }
        </Card>
    )

    function hasAgentInformation() {
        if (isObjectNull(shipDetails)) return false;
        if (isObjectNull(shipDetails.agentInformation)) return false;

        const agentInformation = shipDetails.agentInformation;

        return  !isNullOrEmpty(agentInformation.agentName) ||
            !isNullOrEmpty(agentInformation.agentAssistancePhone) ||
            !isNullOrEmpty(agentInformation.agentContactPerson) ||
            !isNullOrEmpty(agentInformation.agentContactPhone);
    }

    function hasPilotage() {
        if (isObjectNull(shipDetails)) return false;
        return !isObjectNull(shipDetails.pilotageInformation);
    }

    function formatAgentInformation(info:string) {
        if (!isNullOrEmpty(info)) return info;
        return "Ikke registrert";
    }
}

interface PilotTransportOrderInformationProps {
    shipDetails: any
}

const PilotTransportOrderInformation = ({ shipDetails }: PilotTransportOrderInformationProps) => {

    if (isObjectNull(shipDetails)) return;

    if (isArrayEmpty(shipDetails.transportOrderInformation)) return;

    return shipDetails.transportOrderInformation.map((transportOrder: any, index: number) => (
        <div key={index}>
            <Spacer height={15} />
            <Card title="Tilbringerordre">
                <div className="map-dialog-container">
                    <div className={`${styles.transportOrderHeader} m-b-10`}>
                        <div>
                            <MiniCard
                                item={{
                                    borderColor: getPilotAssignmentTransportOrderBorderColor(
                                        transportOrder.transportOrderStatus,
                                    ),
                                    text: `${getTimeType(transportOrder)} - ${transportOrder.transportOrderStatusName.toLowerCase()}`,
                                }}
                            />
                        </div>
                        <div>
                            <a href={`tel: ${transportOrder.transportContactMobilePhone}`} className="pilot-phone-link">
                                {`${transportOrder.mobilePhone}`}
                            </a>
                        </div>
                    </div>
                    <div className="m-b-5">Fra: {transportOrder.fromLocationName}</div>
                    <div className="m-b-5">Til: {transportOrder.toLocationName}</div>
                </div>
            </Card>
        </div>
    ));

    function getTimeType(transportOrder:any) {
        if (transportOrder.transportOrderTimeType === "DELIVER_TIME") return "Levering";
        return "Henting";
    }
}

interface PilotInformationProps {
    shipDetails: any
}

const PilotInformation = ({ shipDetails }: PilotInformationProps) => {

    if (isObjectNull(shipDetails)) return;
    if (isArrayEmpty(shipDetails.pilotInformation)) return;

    return (
        <>
            <Spacer height={15} />
            <Card title="Loser">
                <div className="map-dialog-container">
                    {
                        shipDetails.pilotInformation.map((pilot:any, index:number) =>
                            <div className={`${styles.pilotInfoRow} m-b-5`} key={index}>
                                <div>
                                    <MiniCard
                                        item={{
                                            borderColor: getPilotAssignmentStatusColorBySystemName(pilot.pilotagePilotStatus),
                                            backgroundColor: getPilotAssignmentStatusColorRgbaBySystemName(pilot.pilotagePilotStatus),
                                            text: `${pilot.lastname} ${pilot.initials}`,
                                            isExaminer: pilot.isExaminer,
                                            isTrainee: pilot.isTrainee,
                                            isApprentice: pilot.isApprentice
                                        }}
                                    />
                                </div>
                                <div>
                                    <a href={`tel: ${pilot.phoneNumber}`} className="pilot-phone-link">
                                        {`${pilot.phoneNumber}`}
                                    </a>
                                </div>
                            </div>
                        )
                    }
                </div>
            </Card>
        </>
    )
}
